import { ChangeDetectionStrategy, Component, ElementRef, input, signal, viewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ValidationControlComponent } from '@shared/validation';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { TranslocoDirective } from '@jsverse/transloco';
import { SELECT_SEARCH_BOX_THRESHOLD } from '@core/const';

@Component({
  selector: 'sky-select',
  imports: [
    MatLabel,
    MatFormField,
    MatSelectModule,
    ReactiveFormsModule,
    ValidationControlComponent,
    MatInput,
    FormsModule,
    MatIcon,
    MatIconButton,
    TranslocoDirective,
  ],
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent<T> {
  control = input.required<FormControl>();
  options = input.required<T[]>();
  label = input<string>();
  optionLabel = input<keyof T | ((item: T) => string)>();
  optionValue = input<keyof T | ((item: T) => T)>();
  placeholder = input<string>();
  isMultiple = input<boolean>(false);
  compareWith = input<((a: T, b: T) => boolean) | null>(null);
  noneOptionLabel = input<string>('');
  disabled = input<boolean>(false);
  hint = input<string>('');
  showError = input<boolean, BooleanInput>(true, {
    transform: coerceBooleanProperty,
  });

  private readonly searchbox = viewChild<ElementRef<MatInput>>('searchbox');

  searchText = signal('');

  protected readonly selectSearchBoxThreshold = SELECT_SEARCH_BOX_THRESHOLD;

  private readonly defaultCompare = (a: T, b: T) => a === b;

  get formattedOptions(): { value: T; label: string; hidden: boolean }[] {
    const valueGetter = this.optionValue() || 'value';
    const labelGetter = this.optionLabel() || 'label';

    return (this.options() || []).map(item => {
      const value =
        typeof valueGetter === 'function' ? valueGetter(item) : (item[valueGetter as keyof T] as unknown as T);
      const label = typeof labelGetter === 'function' ? labelGetter(item) : String(item[labelGetter as keyof T]);

      const hidden = !!this.searchText() && !label.toLowerCase().startsWith(this.searchText().toLowerCase());

      return { value, label, hidden };
    });
  }

  get compareWithFn(): (a: T, b: T) => boolean {
    return this.compareWith() || this.defaultCompare;
  }

  get isRequired() {
    return this.control().hasValidator(Validators.required);
  }

  onSelectionChange() {
    this.resetSearch();
  }

  resetSearch() {
    this.searchText.set('');
  }

  onOpen(status: boolean) {
    if (status) {
      // wait for overlay
      setTimeout(() => {
        if (this.searchbox()) {
          this.searchbox().nativeElement.focus();
        }
      });
    } else {
      this.resetSearch();
    }
  }
}
